"use client";

import type React from "react";
import { useId } from "react";
import Checkbox, { type CheckboxProps } from "src/components/form/Checkbox/Checkbox";
import FormControl, {
  type FormControlMessageProps,
  type FormControlProps,
} from "src/components/form/FormControl/FormControl";
import Label from "src/components/form/Label/Label";

export type CheckboxFieldProps = FormControlProps &
  CheckboxProps & {
    type?: string;
    placeholder?: string;
    enterKeyHint?: React.InputHTMLAttributes<any>["enterKeyHint"];
    className?: string;
    controlClassName?: string;
    inputRef?: CheckboxProps["ref"];
    hideLabel?: boolean;
    message?: React.ReactNode;
    messageProps?: FormControlMessageProps;
  };

const CheckboxField = (props: CheckboxFieldProps) => {
  const {
    name,
    id,
    className,
    controlClassName,
    defaultValue,
    value,
    disabled,
    hideLabel,
    onValueProcess,
    label,
    error,
    validate,
    inputRef,
    required,
    message,
    messageProps,
    ...otherProps
  } = props;
  const generatedId = useId();
  const controlId = id ?? generatedId;
  const showLabel = !hideLabel && Boolean(label);

  return (
    <FormControl
      disabled={disabled}
      required={required}
      defaultValue={defaultValue}
      onValueProcess={onValueProcess}
      value={value}
      error={error}
      label={label}
      name={name}
      id={controlId}
      validate={validate}
      className={className}
    >
      <div className="grid grid-cols-[auto_minmax(0,1fr)] items-start gap-2">
        <div className="flex items-center">
          <FormControl.Input>
            <Checkbox
              ref={inputRef}
              disabled={disabled}
              className={controlClassName}
              {...otherProps}
              aria-labelledby={
                otherProps["aria-labelledby"] ?? (showLabel ? `${controlId}-label` : undefined)
              }
              aria-label={
                otherProps["aria-label"] ??
                (!showLabel && typeof label === "string" ? label : undefined)
              }
            />
          </FormControl.Input>
        </div>
        <div className="flex min-w-0 flex-col gap-form-control has-[>label:only-child]:self-center">
          {showLabel ? <Label id={`${controlId}-label`} variant="option" /> : null}
          <FormControl.Message {...messageProps}>{message}</FormControl.Message>
        </div>
      </div>
    </FormControl>
  );
};

export default CheckboxField;
